import { HTMLProps, memo, ReactNode, useEffect, useMemo, useRef } from "react";
import { usePickerActions, usePickerData } from "./picker";
import { useColumnData } from "./picker-column";

interface PickerItemRenderProps {
  selected: boolean;
}

export interface PickerItemProps extends Omit<HTMLProps<HTMLDivElement>, "value" | "children"> {
  children: ReactNode | ((renderProps: PickerItemRenderProps) => ReactNode);
  value: string | number;
}

// eslint-disable-next-line
function isFunction(functionToCheck: any): functionToCheck is Function {
  return typeof functionToCheck === "function";
}

const PickerItem = memo(function PickerItem({
  style,
  children,
  value,
  ...restProps
}: PickerItemProps) {
  const optionRef = useRef<HTMLDivElement | null>(null);
  const { itemHeight, value: pickerValue } = usePickerData("Picker.Item");
  const pickerActions = usePickerActions("Picker.Item");
  const { key } = useColumnData("Picker.Item");

  useEffect(
    () => pickerActions.registerOption(key, { value, element: optionRef }),
    [key, pickerActions, value],
  );

  const itemStyle = useMemo(
    () => ({
      height: `${itemHeight}px`,
    }),
    [itemHeight],
  );

  // React synthetic onClick never fires inside IonModal/IonNav subtrees (same
  // delegation issue as PickerColumn touch events). Native listener works everywhere.
  // A swipe through the column calls preventDefault() on touchmove, which suppresses
  // the subsequent click — so this only fires on deliberate taps.
  const latestRef = useRef({ pickerActions, key, value });
  latestRef.current = { pickerActions, key, value };
  useEffect(() => {
    const el = optionRef.current;
    if (!el) return;
    const handler = () => {
      const { pickerActions, key, value } = latestRef.current;
      pickerActions.change(key, value);
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, []);

  return (
    <div
      className="flex items-center justify-center"
      style={{
        ...itemStyle,
        ...style,
      }}
      ref={optionRef}
      {...restProps}
    >
      {isFunction(children) ? children({ selected: pickerValue[key] === value }) : children}
    </div>
  );
});

export default PickerItem;
